import { isActionCurrent } from './dispatchOwnership';
import { Ref, ref, watch } from '@vue/composition-api';
import { createRealTimeConnection } from './realtimeService';
import { IAction, IActionDispatcher, reducerFn, middlewareFn, IRealTimeStoreState } from './types';
//import { send, subcribe } from './realtimeService';

//Action dispatchert.....
class ActionDispatcher<TState, TAction extends IAction<TState>> implements IActionDispatcher<TAction> {
    protected reducers: Map<string, reducerFn<TState, TAction>> = new Map();
    protected middlewares: Map<string, Array<middlewareFn<TState, TAction>>> = new Map();

    protected state: Ref<TState> | null = null;

    protected realtimeConnection: any = null;

    constructor(state: Ref<TState>) {
        this.state = state;
    }

    public setRealTimeConnection(connection: any) {
        this.realtimeConnection = connection;
    }

    public registerReducer(actionType: string, fn: reducerFn<TState, TAction>) {
        //console.log('Reg reducer: ', actionType);
        if (this.reducers.has(actionType)) {
            throw new Error('[FluxStore]: There is already registered a reducer for: ' + actionType);
        } else {
            this.reducers.set(actionType, fn);
        }
    }

    public regMiddleware(actionType: string, fn: middlewareFn<TState, TAction>): void {
        //console.log('Reg middleware: ', actionType);
        if (this.middlewares.has(actionType)) {
            this.middlewares.get(actionType).push(fn);
        } else {
            this.middlewares.set(actionType, [fn]);
        }
    }

    public dispatchRealtime = async (action: any): Promise<boolean> => {
        action.isDispatchedByRealTimeCallback = true;
        return this.dispatch(action);
    };

    public dispatch = async (action: TAction): Promise<boolean> => {
        const key = action.actionType;

        let callNext = true;
        if (this.middlewares.has(key)) {
            const actionMiddlewares = this.middlewares.get(key);
            for (let index = 0; index < actionMiddlewares.length; index++) {
                if (callNext && isActionCurrent(action)) {
                    const midleware = actionMiddlewares[index];

                    callNext = await midleware(this.state.value, action);
                }
            }
        }

        if (this.reducers.has(key) && callNext && isActionCurrent(action)) {
            const reducer = this.reducers.get(key);
            reducer(this.state.value, action);

            this.sendRealtime(action);
            return true;
        }

        return false; //TODO: biztos?
    };

    private sendRealtime(action: TAction) {
        if (isActionCurrent(action) && this.shouldSendRealtime(action)) {
            if (!this.realtimeConnection) {
                return;
            }

            if ((this.state.value as IRealTimeStoreState).isPublishedRecipient) {
                return;
            }

            //@ts-ignore
            action.correlationId = this.state.value.correlationId;
            this.realtimeConnection.send(action);
        }
    }

    private shouldSendRealtime(arg: any): boolean {
        return typeof arg.isDispatchedByRealTimeCallback == 'boolean' && arg.isDispatchedByRealTimeCallback === false;
    }
}

export function createStoreModule<TState, TActionTypes extends IAction<TState>>(stateFactory: () => TState, id: string = '') {
    const state = ref<TState>(stateFactory());
    const connectionStatus = ref('connecting');
    const dispatcher = new ActionDispatcher<TState, TActionTypes>(state as Ref<TState>);

    //Ezz még nem úgy megy ahogy annak kellene....
    function resetModule() {
        //state.value = stateFactory() as any;
        store.state = stateFactory() as any;
        //Object.assign(state.value, stateFactory());
    }

    let realTimeConnection: any = null;
    //subcribe(dispatcher.dispatchRealtime.bind(dispatcher));
    if (
        (state.value as IRealTimeStoreState).isInitialized !== undefined &&
        (state.value as IRealTimeStoreState).correlationId !== undefined &&
        (state.value as IRealTimeStoreState).storeType !== undefined
    ) {
        watch(
            //@ts-ignore
            () => state.value.isInitialized,
            (current: boolean) => {
                if (current) {
                    const realTimeState = state.value as IRealTimeStoreState;
                    if (realTimeState.isPublishedRecipient) {
                        connectionStatus.value = 'disconnected';
                        return;
                    }

                    realTimeConnection = createRealTimeConnection(
                        realTimeState.correlationId,
                        realTimeState.storeType
                    );
                    dispatcher.setRealTimeConnection(realTimeConnection);

                    realTimeConnection.subcribe(dispatcher.dispatchRealtime.bind(dispatcher));

                    realTimeConnection.connect();

                    realTimeConnection.onConnectionStatusChange((status: string) => {
                        connectionStatus.value = status;
                    });
                } else {
                    if (realTimeConnection) {
                        realTimeConnection.disConnect();
                    }
                }
            },
            { immediate: false }
        );
    }

    const store = {
        id,
        state: state.value,
        resetModule,
        registerMiddleware: dispatcher.regMiddleware.bind(dispatcher),
        registerReducer: dispatcher.registerReducer.bind(dispatcher),
        dispatch: dispatcher.dispatch,
        connectionStatus,
    };

    // make state access invisible
    Object.defineProperty(store, 'state', {
        get: () => state.value,
        set: (newState: TState) => {
            state.value = newState as any;
        },
    });

    return function useStore() {
        return store;
    };
}
